/**
 * Browser-side session recorder. Phase 3 of the Sessions Reporting feature.
 *
 * Owns the session lifecycle for the agent-runtime: buffers transcript turns
 * + events, creates a session after the user hits play, periodically flushes
 * to the API, and finalises with a `/end` call (or a proof-authenticated
 * keepalive request on pagehide). Standalone error-class events post
 * immediately after a session exists — they're rare and we want them on the
 * wire before the page might close.
 *
 * The recorder is provider-agnostic: `OpenAIRealtimeSession` and a future
 * Vapi adapter both call into the same `recordTurn` / `recordEvent` API.
 *
 * Failure handling: every fetch is best-effort. Failed batches push back
 * onto the head of the buffer for a bounded retry. After 3 consecutive
 * failures we drop the batch with a Sentry warning — analytics-data loss
 * under sustained network pain is acceptable; this is observability data,
 * not transactional.
 *
 * The page may be torn down between any two messages; never assume close()
 * runs. The pagehide listener uses navigator.sendBeacon for the final
 * flush since fetch is unreliable during teardown.
 */

import {
  SessionCreateResponseSchema,
  type SessionAppendEventsRequest,
  type SessionAppendTurnsRequest,
  type SessionEventInput,
  type SessionEndRequest,
  type TranscriptTurnInput,
} from '@cloneai/shared-types';

export interface SessionCredentials {
  readonly sessionId: string;
  readonly sessionToken: string;
  readonly sessionTokenExpiresAt: string;
}

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 25;
const MAX_BUFFER = 1000;
const MAX_RETRIES_PER_BATCH = 3;
const FORCE_FLUSH_THRESHOLD = 200;

interface RecorderConfig {
  readonly apiBaseUrl: string;
  readonly tenantSlug: string;
  readonly productSlug: string;
  readonly productType?: 'agent';
  readonly origin?: string | null;
}

type CloseSignal = { kind: 'completed' } | { kind: 'abandoned' } | { kind: 'cta-clicked' };

export class SessionRecorder {
  private sessionId: string | null = null;
  // Reserved for render/TTS calls that incur compute cost. Never persist it
  // or add it to the best-effort analytics requests below.
  private sessionCredentials: SessionCredentials | null = null;
  private sessionPromise: Promise<string | null> | null = null;
  private turnsBuffer: TranscriptTurnInput[] = [];
  private eventsBuffer: SessionEventInput[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  // Tracks whether the pagehide path has already sent an `abandoned`
  // /end call so a follow-up `close()` doesn't double-end (the server is
  // idempotent on `endedAt`, but skipping the duplicate save is cheaper).
  private endedBeaconed = false;
  private pageHideListener: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private ctaClicked = false;
  private voiceStartedAtMs: number | null = null;
  private voiceEnded = false;
  private textStartedAtMs: number | null = null;
  private textEnded = false;

  constructor(private readonly cfg: RecorderConfig) {
    this.flushTimer = setInterval(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);

    // Final analytics flush on page teardown via sendBeacon. The handler must be
    // synchronous — the browser kills the page if you await anything.
    //
    // pagehide   → fires when the tab is closed / navigated away. The page
    //              is going away for sure, so we beacon both buffers AND
    //              an `/end` with outcome=abandoned.
    // visibility → fires earlier (tab switch / minimize). Most reliable
    //   change       on Safari + iOS where pagehide is sometimes skipped.
    //              We beacon-flush the buffers but DO NOT end the session
    //              here — the user may come back. The hidden state is also
    //              the only chance we get on iOS to land in-flight turns,
    //              so flushing eagerly here is the high-value action.
    this.pageHideListener = (): void => {
      this.recordImplicitVoiceEnd('pagehide');
      this.recordImplicitTextEnd('pagehide');
      this.beaconFlush();
      this.beaconEnd('abandoned');
    };
    this.visibilityListener = (): void => {
      if (document.visibilityState === 'hidden') this.beaconFlush();
    };
    window.addEventListener('pagehide', this.pageHideListener);
    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  /**
   * Kicks off session creation. Called from the play gesture (or, defensively,
   * voice activation); subsequent calls are no-ops. Any page-load events that
   * were buffered before start are flushed once the sessionId exists.
   */
  async start(): Promise<void> {
    if (this.sessionPromise) return;
    this.sessionPromise = this.createSession();
    const sessionId = await this.sessionPromise;
    if (!sessionId) {
      this.sessionPromise = null;
      return;
    }
    if (!this.closed) await this.flush();
  }

  recordTurn(turn: TranscriptTurnInput): void {
    if (this.closed) return;
    this.turnsBuffer.push(turn);
    this.maybeForceFlush();
  }

  recordEvent(event: SessionEventInput): void {
    if (this.closed) return;
    this.eventsBuffer.push(this.normalizeEvent(event));
    this.maybeForceFlush();
  }

  /** Post a single error-class event immediately (bypassing the batch timer). */
  recordImmediateEvent(event: SessionEventInput): void {
    if (this.closed) return;
    this.eventsBuffer.push(this.normalizeEvent(event));
    void this.flushEvents();
  }

  /** Return the existing browser session id, creating nothing by itself. */
  async getSessionId(): Promise<string | null> {
    return this.ensureSessionId();
  }

  /** Return the in-memory proof for paid session operations, creating nothing. */
  async getSessionCredentials(): Promise<SessionCredentials | null> {
    await this.ensureSessionId();
    return this.sessionCredentials;
  }

  /**
   * Final flush + /end. Called from the outer page on session-end / unmount.
   * Idempotent. Disarms the periodic flush + pagehide listener.
   */
  async close(signal: CloseSignal): Promise<void> {
    if (this.closed) return;
    this.recordImplicitVoiceEnd('close');
    this.recordImplicitTextEnd('close');
    this.closed = true;
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pageHideListener) {
      window.removeEventListener('pagehide', this.pageHideListener);
      this.pageHideListener = null;
    }
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }

    await this.flush();
    await this.endSession(signal);
    this.sessionCredentials = null;
  }

  private async createSession(): Promise<string | null> {
    try {
      const response = await fetch(`${this.cfg.apiBaseUrl}/v1/public/sessions`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({
          productType: this.cfg.productType ?? 'agent',
          tenantSlug: this.cfg.tenantSlug,
          productSlug: this.cfg.productSlug,
          origin: this.cfg.origin ?? null,
        }),
      });
      if (!response.ok) {
        console.warn('[sessionRecorder] /sessions create failed', response.status);
        return null;
      }
      const json = SessionCreateResponseSchema.parse(await response.json());
      this.sessionId = json.sessionId;
      this.sessionCredentials = {
        sessionId: json.sessionId,
        sessionToken: json.sessionToken,
        sessionTokenExpiresAt: json.sessionTokenExpiresAt,
      };
      return this.sessionId;
    } catch (err) {
      console.warn('[sessionRecorder] /sessions create error', err);
      return null;
    }
  }

  private normalizeEvent(event: SessionEventInput): SessionEventInput {
    if (event.type === 'cta-click') {
      this.ctaClicked = true;
    }

    if (event.type === 'voice-start') {
      const startedMs = Date.parse(event.timestamp);
      this.voiceStartedAtMs = Number.isNaN(startedMs) ? Date.now() : startedMs;
      this.voiceEnded = false;
    }

    if (event.type === 'voice-end') {
      this.voiceEnded = true;
      const payload = { ...(event.payload ?? {}) };
      if (payload['durationSec'] === undefined && this.voiceStartedAtMs !== null) {
        const endedMs = Date.parse(event.timestamp);
        const durationMs = (Number.isNaN(endedMs) ? Date.now() : endedMs) - this.voiceStartedAtMs;
        payload['durationSec'] = Math.max(0, Math.round(durationMs / 1000));
      }
      return { ...event, payload };
    }

    if (event.type === 'text-chat-start') {
      const startedMs = Date.parse(event.timestamp);
      this.textStartedAtMs = Number.isNaN(startedMs) ? Date.now() : startedMs;
      this.textEnded = false;
    }

    if (event.type === 'text-chat-end') {
      this.textEnded = true;
      const payload = { ...(event.payload ?? {}) };
      if (payload['durationSec'] === undefined && this.textStartedAtMs !== null) {
        const endedMs = Date.parse(event.timestamp);
        const durationMs = (Number.isNaN(endedMs) ? Date.now() : endedMs) - this.textStartedAtMs;
        payload['durationSec'] = Math.max(0, Math.round(durationMs / 1000));
      }
      return { ...event, payload };
    }

    return event;
  }

  private recordImplicitVoiceEnd(reason: 'close' | 'pagehide'): void {
    if (this.voiceStartedAtMs === null || this.voiceEnded) return;
    this.eventsBuffer.push(
      this.normalizeEvent({
        type: 'voice-end',
        timestamp: new Date().toISOString(),
        payload: { reason },
      }),
    );
  }

  private recordImplicitTextEnd(reason: 'close' | 'pagehide'): void {
    if (this.textStartedAtMs === null || this.textEnded) return;
    this.eventsBuffer.push(
      this.normalizeEvent({
        type: 'text-chat-end',
        timestamp: new Date().toISOString(),
        payload: { reason },
      }),
    );
  }

  private resolveOutcome(outcome: CloseSignal['kind']): CloseSignal['kind'] {
    return this.ctaClicked ? 'cta-clicked' : outcome;
  }

  private async ensureSessionId(): Promise<string | null> {
    if (this.sessionId) return this.sessionId;
    if (this.sessionPromise) return this.sessionPromise;
    return null;
  }

  /** Periodic flush — drains both buffers when non-empty. */
  private async flush(): Promise<void> {
    await Promise.all([this.flushTurns(), this.flushEvents()]);
  }

  private async flushTurns(): Promise<void> {
    if (this.turnsBuffer.length === 0) return;
    const sessionId = await this.ensureSessionId();
    if (!sessionId) return;

    const batch = this.turnsBuffer.splice(0, MAX_BATCH);
    const body: SessionAppendTurnsRequest = { turns: batch };
    const ok = await this.postWithRetry(
      `/v1/public/sessions/${encodeURIComponent(sessionId)}/turns`,
      body,
    );
    if (!ok) {
      // Push back to head — bounded buffer.
      this.turnsBuffer = [...batch, ...this.turnsBuffer].slice(0, MAX_BUFFER);
    }
  }

  private async flushEvents(): Promise<void> {
    if (this.eventsBuffer.length === 0) return;
    const sessionId = await this.ensureSessionId();
    if (!sessionId) return;

    const batch = this.eventsBuffer.splice(0, MAX_BATCH);
    const body: SessionAppendEventsRequest = { events: batch };
    const ok = await this.postWithRetry(
      `/v1/public/sessions/${encodeURIComponent(sessionId)}/events`,
      body,
    );
    if (!ok) {
      this.eventsBuffer = [...batch, ...this.eventsBuffer].slice(0, MAX_BUFFER);
    }
  }

  private async postWithRetry(path: string, body: unknown): Promise<boolean> {
    for (let attempt = 0; attempt < MAX_RETRIES_PER_BATCH; attempt += 1) {
      try {
        const response = await fetch(`${this.cfg.apiBaseUrl}${path}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (response.ok) return true;
        // 4xx = client bug, won't recover by retrying. Drop with a warn.
        if (response.status >= 400 && response.status < 500) {
          console.warn('[sessionRecorder] non-retryable status', response.status, path);
          return true;
        }
      } catch {
        // network — fall through to retry
      }
    }
    return false;
  }

  private maybeForceFlush(): void {
    if (
      this.turnsBuffer.length >= FORCE_FLUSH_THRESHOLD ||
      this.eventsBuffer.length >= FORCE_FLUSH_THRESHOLD
    ) {
      void this.flush();
    } else if (this.turnsBuffer.length >= MAX_BATCH || this.eventsBuffer.length >= MAX_BATCH) {
      void this.flush();
    }
  }

  /**
   * pagehide path. sendBeacon is fire-and-forget and survives page teardown
   * where regular fetch does not. We don't bother with retries here — the
   * page is going away and the browser drops queued requests anyway.
   */
  /** Beacon any pending turn / event batches. Safe to call repeatedly. */
  private beaconFlush(): void {
    if (!this.sessionId) return;
    if (this.turnsBuffer.length > 0) {
      const body: SessionAppendTurnsRequest = { turns: this.turnsBuffer };
      navigator.sendBeacon(
        `${this.cfg.apiBaseUrl}/v1/public/sessions/${encodeURIComponent(this.sessionId)}/turns`,
        JSON.stringify(body),
      );
      this.turnsBuffer = [];
    }
    if (this.eventsBuffer.length > 0) {
      const body: SessionAppendEventsRequest = { events: this.eventsBuffer };
      navigator.sendBeacon(
        `${this.cfg.apiBaseUrl}/v1/public/sessions/${encodeURIComponent(this.sessionId)}/events`,
        JSON.stringify(body),
      );
      this.eventsBuffer = [];
    }
  }

  /**
   * Attempt a proof-authenticated keepalive `/end` request so an abandoned
   * session can release paid capacity as well as its analytics lifecycle. The
   * short demand-lease expiry remains the teardown backstop. The /end handler
   * is idempotent on `endedAt`,
   * so a later graceful `close({ kind: 'completed' })` that loses the
   * race remains a no-op (the abandoned outcome wins). The
   * `endedBeaconed` flag suppresses duplicate beacons within the same
   * page lifetime.
   */
  private beaconEnd(outcome: CloseSignal['kind']): void {
    if (!this.sessionId || !this.sessionCredentials || this.endedBeaconed) return;
    this.endedBeaconed = true;
    const body: SessionEndRequest = {
      endedAt: new Date().toISOString(),
      outcome: this.resolveOutcome(outcome),
    };
    void fetch(
      `${this.cfg.apiBaseUrl}/v1/public/sessions/${encodeURIComponent(this.sessionId)}/end`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'X-CloneAI-Session-Token': this.sessionCredentials.sessionToken,
        },
        body: JSON.stringify(body),
        keepalive: true,
      },
    ).catch((err: unknown) => {
      console.warn('[sessionRecorder] keepalive /end failed', err);
    });
  }

  private async endSession(signal: CloseSignal): Promise<void> {
    if (!this.sessionId || !this.sessionCredentials || this.endedBeaconed) return;
    const outcome = this.resolveOutcome(signal.kind);
    const body: SessionEndRequest = {
      endedAt: new Date().toISOString(),
      outcome,
    };
    try {
      await fetch(
        `${this.cfg.apiBaseUrl}/v1/public/sessions/${encodeURIComponent(this.sessionId)}/end`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            'X-CloneAI-Session-Token': this.sessionCredentials.sessionToken,
          },
          body: JSON.stringify(body),
          keepalive: true,
        },
      );
    } catch (err) {
      console.warn('[sessionRecorder] /end failed', err);
    }
  }
}
